import {useCallback} from 'react'
import {useSelectedVariantContext} from '../components/step2/context/SelectedVariantContext'
import {
    actionChangeSafeZoneImage,
    actionUpdateBackgroundImage,
} from '../components/step2/context/SelectedVariantContext/action'
import {useEditorBackground} from './useEditorBackground'
import {useEditorSides} from './useEditorSides'
import PubSub from '../components/step2/Broker'
import {createDebugger} from '../helpers/Debugger'

const debug = createDebugger({namespace: 'useEditorSafeZone'})

/**
 * Horizontally flip an image and return its data URL.
 * @param {string|{ src: string }} imageSource - Image URL or object with src
 * @returns {Promise<string>} Data URL of the flipped image (image/png)
 */
export const horizontalFlipImage = (imageSource) => {
    const src = typeof imageSource === 'string' ? imageSource : imageSource?.src
    if (!src) return Promise.resolve('')

    return new Promise((resolve, reject) => {
        const img = new Image()
        img.crossOrigin = 'anonymous'
        img.onload = () => {
            const canvas = document.createElement('canvas')
            canvas.width = img.width
            canvas.height = img.height
            const ctx = canvas.getContext('2d')
            ctx.translate(canvas.width, 0)
            ctx.scale(-1, 1)
            ctx.drawImage(img, 0, 0)
            resolve(canvas.toDataURL('image/png'))
        }
        img.onerror = () => reject(new Error('Failed to load image for horizontal flip'))
        img.src = src
    })
}

const useEditorSafeZone = () => {
    const {state, dispatch} = useSelectedVariantContext()
    const {selectedSide} = state
    const {sides} = useEditorSides()
    const background = useEditorBackground()

    /**
     * Set Safe Zone image props
     */
    const setSafeZone = useCallback(
        async (nextSafeZone) => {
            const otherSide = sides.filter((side) => side !== selectedSide)[0]
            const currentSafeZone = state?.safeZone?.[selectedSide] || {}
            const next = typeof nextSafeZone === 'function' ? nextSafeZone(currentSafeZone) : nextSafeZone
            const horizontalFlipSafeZone = await horizontalFlipImage(next.src)
            dispatch(
                actionChangeSafeZoneImage({
                    [selectedSide]: {
                        ...next,
                        src: next.src,
                    },
                    [otherSide]: {
                        ...next,
                        src: horizontalFlipSafeZone,
                    },
                }),
            )
            PubSub.publish('UPDATE_CUSTOM_SHAPE_CUT_SIDE', [{
                src: next.src,
                side: selectedSide,
            }])
        },
        [dispatch, state?.safeZone, selectedSide, sides],
    )

    /**
     * Clear Safe Zone image
     */
    const clearSafeZone = useCallback(
        () => dispatch(actionChangeSafeZoneImage(null)),
        [dispatch, actionChangeSafeZoneImage],
    )

    /**
     * Set Background image
     */
    const setBackground = useCallback(
        (image) => dispatch(actionUpdateBackgroundImage(image)),
        [dispatch, actionUpdateBackgroundImage],
    )

    return {
        /**
         * Safe Zone image
         */
        safeZone: state?.safeZone?.[selectedSide],
        /**
         * Background image
         */
        background,
        setSafeZone,
        clearSafeZone,
        setBackground,
    }
}

export default useEditorSafeZone
